import type { ReferralActivityRecord } from '@navio/nsdk';
import { ReferralActivityEventType } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiCard, NoddiSpinner, usePaginationQueryParam } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';

type Props = {
  brandId: number;
  serviceOrganizationId: number;
};

const EVENT_META: Record<ReferralActivityEventType, { icon: NoddiIconName; phrase: string }> = {
  [ReferralActivityEventType.CREATED]: { icon: 'Plus', phrase: 'created a referral' },
  [ReferralActivityEventType.UPDATED]: { icon: 'Edit', phrase: 'updated a referral' },
  [ReferralActivityEventType.ACTIVATED]: { icon: 'UserPlus', phrase: 'activated a referral' },
  [ReferralActivityEventType.DELETED]: { icon: 'Trash2', phrase: 'deleted a referral' }
};

export const ReferralProgramActivityTab = ({ brandId, serviceOrganizationId }: Props) => {
  const { pagination } = usePaginationQueryParam({ paramKeyPrefix: 'activity' });

  const { data, isPending, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedPartnerReferralProgramActivities,
    input: { serviceOrganizationId, brandId, ...pagination },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  if (error) {
    return <ApiErrorMessageInternal error={error} />;
  }

  if (isPending) {
    return (
      <div className='flex items-center justify-center py-10'>
        <NoddiSpinner />
      </div>
    );
  }

  const activities = data?.results ?? [];

  return (
    <NoddiCard title='Activity' size='small' icon='Activity' iconTile>
      {activities.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No activity yet.</p>
      ) : (
        <div className='flex flex-col gap-4'>
          {activities.map((activity) => (
            <ActivityRow key={activity.id} activity={activity} />
          ))}
        </div>
      )}
    </NoddiCard>
  );
};

const ActivityRow = ({ activity }: { activity: ReferralActivityRecord }) => {
  const actorName = activity.actorName ?? 'System';
  const timestamp = noddiFormatDate(activity.createdAt, NoddiDateFormats.DATE_TIME);
  const eventType = activity.eventType.value;
  const meta = EVENT_META[eventType];
  const icon = meta?.icon ?? 'Circle';
  const phrase = meta?.phrase ?? eventType.toLowerCase().replaceAll('_', ' ');
  const detail = activity.detail?.trim() || null;

  return (
    <div className='flex items-start gap-3'>
      <div className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted')}>
        <NoddiIcon name={icon} size='small' className='text-muted-foreground' />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm'>
          <span className='font-medium'>{actorName}</span>
          <span className='text-muted-foreground'>{phrase}</span>
          <span className='ml-auto text-xs text-muted-foreground'>{timestamp}</span>
        </div>
        {detail ? <p className='text-xs whitespace-pre-wrap text-muted-foreground'>{detail}</p> : null}
      </div>
    </div>
  );
};
